import React, { useEffect, useState } from "react";
import "../../assets/styles/card.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function WiFiPreferences({ customerId = null }) {
  const isAdminMode = Boolean(customerId);
  const [wifiList, setWifiList] = useState([]);
  const [wifiLoading, setWifiLoading] = useState(false);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [editingWifi, setEditingWifi] = useState(null);
  const [wifiForm, setWifiForm] = useState({
    wifi_name: "",
    ssid: "",
    password: "",
    priority: ""
  });

  useEffect(() => {
    const fetchWifiList = async () => {
      setWifiLoading(true);
      try {
        const token = localStorage.getItem("jwt_token");
        const url = isAdminMode
          ? `${BASE_URL}/api/preferences/admin/wifi-list/?cust_id=${customerId}`
          : `${BASE_URL}/api/preferences/wifi-list/`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const wifiData = data.data || [];

          const sortedByDate = [...wifiData].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : a.id;
            const dateB = b.created_at ? new Date(b.created_at) : b.id;
            return dateB - dateA;
          });

          const recent30Ids = new Set(sortedByDate.slice(0, 30).map(w => w.id));

          const wifiWithFlags = wifiData.map(wifi => ({
            ...wifi,
            isRecent: recent30Ids.has(wifi.id)
          }));

          const sortedList = wifiWithFlags.sort((a, b) => a.priority - b.priority);
          setWifiList(sortedList);
        } else {
          console.error("Failed to fetch WiFi list");
        }
      } catch (err) {
        console.error("Error fetching WiFi list:", err);
      } finally {
        setWifiLoading(false);
      }
    };

    fetchWifiList();
  }, [customerId, isAdminMode]);

  const refreshWifiList = async (token) => {
    const listUrl = isAdminMode
      ? `${BASE_URL}/api/preferences/admin/wifi-list/?cust_id=${customerId}`
      : `${BASE_URL}/api/preferences/wifi-list/`;
    const listRes = await fetch(listUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (listRes.ok) {
      const listData = await listRes.json();
      const wifiData = listData.data || [];

      const sortedByDate = [...wifiData].sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : a.id;
        const dateB = b.created_at ? new Date(b.created_at) : b.id;
        return dateB - dateA;
      });

      const recent30Ids = new Set(sortedByDate.slice(0, 30).map(w => w.id));

      const wifiWithFlags = wifiData.map(wifi => ({
        ...wifi,
        isRecent: recent30Ids.has(wifi.id)
      }));

      const sortedList = wifiWithFlags.sort((a, b) => a.priority - b.priority);
      setWifiList(sortedList);
    }
  };

  const handleOpenWifiModal = (wifi = null) => {
    if (wifi) {
      setEditingWifi(wifi);
      setWifiForm({
        wifi_name: wifi.wifi_name,
        ssid: wifi.ssid,
        password: wifi.password,
        priority: wifi.priority
      });
    } else {
      setEditingWifi(null);
      const maxPriority = wifiList.length > 0 ? Math.max(...wifiList.map(w => w.priority || 0)) : 0;
      setWifiForm({ wifi_name: "", ssid: "", password: "", priority: maxPriority + 1 });
    }
    setShowWifiModal(true);
  };

  const handleCloseWifiModal = () => {
    setShowWifiModal(false);
    setEditingWifi(null);
    setWifiForm({ wifi_name: "", ssid: "", password: "", priority: "" });
  };

  const handleWifiFormChange = (e) => {
    setWifiForm({ ...wifiForm, [e.target.name]: e.target.value });
  };

  const handleSaveWifi = async () => {
    if (!wifiForm.wifi_name || !wifiForm.ssid || !wifiForm.priority) {
      toast.error("WiFi name, SSID, and priority are required");
      return;
    }

    const priority = parseInt(wifiForm.priority);
    if (isNaN(priority) || priority < 1) {
      toast.error("Priority must be a positive number starting from 1");
      return;
    }

    try {
      const token = localStorage.getItem("jwt_token");
      let url = isAdminMode
        ? `${BASE_URL}/api/preferences/admin/wifi-create/`
        : `${BASE_URL}/api/preferences/wifi-create/`;
      let method = "POST";

      if (editingWifi) {
        url = isAdminMode
          ? `${BASE_URL}/api/preferences/admin/wifi/${editingWifi.id}/?cust_id=${customerId}`
          : `${BASE_URL}/api/preferences/wifi/${editingWifi.id}/`;
        method = "PATCH";
      }

      const requestData = {
        ...wifiForm,
        priority: parseInt(wifiForm.priority),
        ...(isAdminMode ? { cust_id: customerId } : {}),
      };

      const res = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (res.ok) {
        toast.success(editingWifi ? "WiFi updated successfully!" : "WiFi added successfully!");
        handleCloseWifiModal();
        await refreshWifiList(token);
      } else {
        const errorData = await res.json();
        toast.error(errorData.error || "Failed to save WiFi");
      }
    } catch (err) {
      console.error("Error saving WiFi:", err);
      toast.error("Error saving WiFi");
    }
  };

  const handleDeleteWifi = async (id) => {
    if (!window.confirm("Are you sure you want to delete this WiFi?")) {
      return;
    }

    try {
      const token = localStorage.getItem("jwt_token");
      const url = isAdminMode
        ? `${BASE_URL}/api/preferences/admin/wifi/${id}/?cust_id=${customerId}`
        : `${BASE_URL}/api/preferences/wifi/${id}/`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success("WiFi deleted successfully!");
        setWifiList(wifiList.filter((wifi) => wifi.id !== id));
      } else {
        const errorData = await res.json();
        toast.error(errorData.error || "Failed to delete WiFi");
      }
    } catch (err) {
      console.error("Error deleting WiFi:", err);
      toast.error("Error deleting WiFi");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <div className="wifi-header">
          <h2 className="pref-card-title">WiFi Preferences</h2>
          <button className="pref-btn-connect" onClick={() => handleOpenWifiModal()}>
            Add WiFi
          </button>
        </div>
        <p className="pref-card-description">
          {isAdminMode
            ? "Manage this customer's WiFi network preferences for device connectivity."
            : "Manage your WiFi network preferences for device connectivity."}
        </p>
        {wifiLoading ? (
          <p className="pref-card-loading">Loading WiFi preferences...</p>
        ) : wifiList.length === 0 ? (
          <p className="pref-card-description">No WiFi networks configured yet.</p>
        ) : (
          <div className="wifi-list">
            {wifiList.map((wifi) => (
              <div
                key={wifi.id}
                className="wifi-item"
                style={{
                  backgroundColor: wifi.isRecent ? 'rgba(0, 255, 0, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                  borderLeft: wifi.isRecent ? '4px solid #00ff00' : '4px solid #808080'
                }}
              >
                <div className="wifi-info">
                  <div className="wifi-name-header">
                    <h3 className="wifi-name">{wifi.wifi_name}</h3>
                    <span className="wifi-priority-badge">Priority: {wifi.priority}</span>
                  </div>
                  <p className="wifi-ssid">SSID: {wifi.ssid}</p>
                </div>
                <div className="wifi-actions">
                  {wifi.id !== 1 && (
                    <button
                      className="wifi-btn-edit"
                      onClick={() => handleOpenWifiModal(wifi)}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    className="wifi-btn-delete"
                    onClick={() => handleDeleteWifi(wifi.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showWifiModal && (
        <div className="wifi-modal-overlay">
          <div className="wifi-modal">
            <h2>{editingWifi ? "Edit WiFi" : "Add WiFi"}</h2>
            <div className="wifi-form">
              <div className="wifi-form-group">
                <label>WiFi Name</label>
                <input
                  type="text"
                  name="wifi_name"
                  value={wifiForm.wifi_name}
                  onChange={handleWifiFormChange}
                  placeholder="e.g., Home WiFi"
                  maxLength={256}
                />
              </div>
              <div className="wifi-form-group">
                <label>SSID</label>
                <input
                  type="text"
                  name="ssid"
                  value={wifiForm.ssid}
                  onChange={handleWifiFormChange}
                  placeholder="Network SSID"
                  maxLength={128}
                />
              </div>
              <div className="wifi-form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={wifiForm.password}
                  onChange={handleWifiFormChange}
                  placeholder="Network Password"
                  maxLength={64}
                />
              </div>
              <div className="wifi-form-group">
                <label>Priority</label>
                <input
                  type="number"
                  name="priority"
                  value={wifiForm.priority}
                  onChange={handleWifiFormChange}
                  placeholder="Priority (1, 2, 3...)"
                  min="1"
                />
              </div>
              <div className="wifi-form-actions">
                <button className="wifi-btn-cancel" onClick={handleCloseWifiModal}>
                  Cancel
                </button>
                <button className="wifi-btn-save" onClick={handleSaveWifi}>
                  {editingWifi ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WiFiPreferences;
